import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { filterByLabel } from "@/lib/filterByLabel";
import {
  buildCustomerSlaConfigurationPayload,
  normalizeCustomerSlaConfigurationList,
  parseCustomerSlaConfigurationToForm,
} from "@/lib/buildCustomerSlaConfigurationPayload";
import { getTenantId } from "@/lib/storeManager";
import DropdownService from "@/services/APIs/DropdownService";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import { useA1ResolvedNav } from "@/hooks/useA1ResolvedNav";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Col, Form, Row, Select, Spin } from "antd";
import { Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./slaSchemaConfigurationFormPage.css";

export interface SLASchemaConfigurationValues {
  sla_policy_schema?: string;
  business_hours_schema?: string;
  holiday_calendar_schema?: string;
  escalation_time_schema?: string;
  notify_role_foreign_key_schema?: string;
}

const storageKey = (tenantId: string) => `slaSchemaConfiguration:${tenantId}`;

const readLocalRecords = (
  tenantId: string
): (SLASchemaConfigurationValues & { id: string })[] => {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [{ id: tenantId, ...parsed }];
  } catch {
    return [];
  }
};

const SLASchemaConfigurationPage = () => {
  const navigate = useNavigate();
  const nav = useA1ResolvedNav();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id")?.trim() || undefined;
  const isEditMode = Boolean(editId);

  const [form] = Form.useForm<SLASchemaConfigurationValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: schemaOptions = [], isLoading: schemaLoading } = useQuery({
    queryKey: ["schema-dropdown", "no-servicedesk-filter"],
    queryFn: () =>
      DropdownService.getDropdownData("schema", undefined, {
        skipServiceDeskParams: true,
      }),
  });

  const { data: editRecord, isLoading: loadingEdit } = useQuery({
    queryKey: ["customer-sla-configuration", editId],
    enabled: isEditMode,
    queryFn: async () => {
      const byId =
        await SLAConfigurationService.getCustomerSlaConfigurationById(editId!);
      if (byId) return byId;

      const list =
        await SLAConfigurationService.listCustomerSlaConfigurations();
      const found = normalizeCustomerSlaConfigurationList(list).find(
        r => String(r.id ?? r.pk ?? "") === editId
      );
      return found ?? null;
    },
  });

  const initialFormValues = useMemo(() => {
    if (!isEditMode || !editId) return null;

    if (editRecord) {
      return parseCustomerSlaConfigurationToForm(editRecord);
    }

    const tenantId = getTenantId();
    if (!tenantId) return null;
    const local = readLocalRecords(tenantId).find(r => r.id === editId);
    return local ?? null;
  }, [isEditMode, editId, editRecord]);

  useEffect(() => {
    if (!initialFormValues) return;
    form.setFieldsValue({
      sla_policy_schema: initialFormValues.sla_policy_schema,
      business_hours_schema: initialFormValues.business_hours_schema,
      holiday_calendar_schema: initialFormValues.holiday_calendar_schema,
      escalation_time_schema: initialFormValues.escalation_time_schema,
      notify_role_foreign_key_schema:
        initialFormValues.notify_role_foreign_key_schema,
    });
  }, [form, initialFormValues]);

  useEffect(() => {
    if (isEditMode && !loadingEdit && !initialFormValues) {
      notify.error("Could not load SLA configuration for edit.");
    }
  }, [isEditMode, loadingEdit, initialFormValues]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const payload = buildCustomerSlaConfigurationPayload(values);
      const saved = isEditMode
        ? await SLAConfigurationService.updateCustomerSlaConfiguration(
            editId!,
            payload
          )
        : await SLAConfigurationService.createCustomerSlaConfiguration(payload);

      if (saved === undefined) {
        return;
      }

      const tenantId = getTenantId();
      if (tenantId) {
        const existing = readLocalRecords(tenantId);
        if (isEditMode) {
          const updated = existing.map(r =>
            r.id === editId ? { id: editId, ...values } : r
          );
          localStorage.setItem(storageKey(tenantId), JSON.stringify(updated));
        } else {
          const newRecord = { id: Date.now().toString(), ...values };
          localStorage.setItem(
            storageKey(tenantId),
            JSON.stringify([...existing, newRecord])
          );
        }
      }

      void queryClient.invalidateQueries({ queryKey: ["customer-sla-configurations"] });
      notify.success(
        isEditMode ? "SLA configuration updated." : "SLA configuration saved."
      );
      form.resetFields();
      navigate(nav.SLA_CONFIGURATION);
    } catch (error: unknown) {
      if (error && typeof error === "object" && "errorFields" in error) {
        notify.error("Please fix validation errors.");
        return;
      }
      notify.error(
        isEditMode
          ? "Failed to update SLA configuration."
          : "Failed to save SLA configuration."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const borderedSectionClass =
    "rounded-lg border border-solid border-[rgba(0,0,0,0.06)] bg-white p-4";

  const schemaSelectProps = {
    allowClear: true,
    showSearch: true,
    filterOption: filterByLabel,
    loading: schemaLoading,
    disabled: isSubmitting || (isEditMode && loadingEdit),
    options: schemaOptions,
    className: "w-full",
  };

  const formLoading = isEditMode && loadingEdit && !initialFormValues;

  return (
    <div className="sla-schema-config-form-page min-w-0 p-4 md:p-0">
      <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit SLA Configuration" : "SLA Configuration"}
          onBack={() => navigate(nav.SLA_CONFIGURATION)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting || formLoading}
              title={isEditMode ? "Update SLA Configuration" : "Save SLA Configuration"}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Spin spinning={formLoading}>
        <Form<SLASchemaConfigurationValues>
          form={form}
          layout="vertical"
        >
          <div className={`sla-config-form-section ${borderedSectionClass} mb-4`}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sla_policy_schema"
                  label={<span className="font-semibold">SLA Policy Schema</span>}
                  rules={[
                    { required: true, message: "Please select SLA policy schema" },
                  ]}
                >
                  <Select {...schemaSelectProps} placeholder="Select Schema" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="business_hours_schema"
                  label={
                    <span className="font-semibold">Business Hours Schema</span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select business hours schema",
                    },
                  ]}
                >
                  <Select {...schemaSelectProps} placeholder="Select Schema" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="holiday_calendar_schema"
                  label={
                    <span className="font-semibold">Holiday Calendar Schema</span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select holiday calendar schema",
                    },
                  ]}
                >
                  <Select {...schemaSelectProps} placeholder="Select Schema" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="escalation_time_schema"
                  label={
                    <span className="font-semibold">Escalation Time Schema</span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select escalation time schema",
                    },
                  ]}
                >
                  <Select {...schemaSelectProps} placeholder="Select Schema" />
                </Form.Item>
              </Col>
            </Row>
            <div className="mb-2 mt-2 font-semibold">Main Apply Schema</div>
            <Form.Item
              name="notify_role_foreign_key_schema"
              label="Schema Name"
              rules={[
                { required: true, message: "Please select Formily schema" },
              ]}
            >
              <Select {...schemaSelectProps} placeholder="Select Formily Schema" />
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </Card>
    </div>
  );
};

export default SLASchemaConfigurationPage;
